import { useMemo } from 'react';
import { useAuth } from '../auth';
import { useWhatsAppChatStore } from '../stores/whatsappChatStore';
import { useMetaMessagingStore } from '../stores/metaMessagingStore';
import { useTeamChatStore, type TeamChat } from '../stores/teamChatStore';
import { useClientStore } from '../stores/clientStore';
import { countUnreadByChannel, mergeChatLists, type UnifiedChatItem, type WhatsAppChatLike, type TeamChatLike } from '../utils/chatListMerging';
import { useMetaChannels } from './useMetaChannels';
import { useWhatsAppChatMetaStore } from '../stores/whatsappChatMetaStore';
import { isMuted } from '../services/whatsapp/chatListRules';

export interface AllConversationsState {
  items: UnifiedChatItem[];
  /** Badge per scheda del selettore. */
  counts: { all: number; whatsapp: number; instagram: number; messenger: number; team: number };
}

/** Nome visibile di una chat di team: nome del gruppo, altrimenti gli altri partecipanti. */
export function teamChatDisplayName(chat: TeamChat, currentUserId: string | undefined): string {
  if (chat.name) return chat.name;
  const others = (chat.participants ?? []).filter((p) => p.user_id !== currentUserId);
  const names = others.map((p) => p.user?.full_name).filter(Boolean) as string[];
  if (names.length) return names.join(', ');
  return chat.is_group ? 'Gruppo' : 'Chat';
}

/**
 * Lista unificata per la vista "Tutte" del drawer Messaggi: WhatsApp (store
 * WAHA), Instagram e Messenger (store Meta, solo se disponibili per l'utente)
 * e chat di team, ordinate per ultima attivita'. Ogni riga porta il canale di
 * provenienza per l'etichetta (decisione Luca 02/09/2026).
 */
export function useAllConversations(options: { includeTeam: boolean; includeWhatsApp: boolean }): AllConversationsState {
  const { userProfile } = useAuth();
  const whatsappChats = useWhatsAppChatStore((s) => s.chats);
  const metaConversations = useMetaMessagingStore((s) => s.conversations);
  const metaReads = useMetaMessagingStore((s) => s.reads);
  const metaIsUnread = useMetaMessagingStore((s) => s.isUnread);
  const teamChats = useTeamChatStore((s) => s.chats);
  const clients = useClientStore((s) => s.clients);
  const leads = useClientStore((s) => s.leads);
  const { available: metaAvailable } = useMetaChannels();
  const chatPrefs = useWhatsAppChatMetaStore((s) => s.chatMeta);

  const metaNames = useMemo(() => {
    const byClient = new Map<string, string>();
    for (const c of clients) byClient.set(c.id, c.business_name);
    for (const l of leads) if (!byClient.has(l.id)) byClient.set(l.id, l.business_name);
    const names = new Map<string, string>();
    for (const conv of metaConversations) {
      if (conv.client_id && byClient.has(conv.client_id)) names.set(conv.id, byClient.get(conv.client_id) as string);
    }
    return names;
  }, [clients, leads, metaConversations]);

  const teamItems = useMemo<TeamChatLike[]>(() => {
    if (!options.includeTeam) return [];
    return teamChats.map((chat) => ({
      id: chat.id,
      name: teamChatDisplayName(chat, userProfile?.id),
      // Chat diretta: foto dell'altro partecipante (gia' normalizzata avatar_url || profile_image_url).
      avatarUrl: chat.is_group ? null : (chat.participants ?? []).find((p) => p.user_id !== userProfile?.id)?.user?.profile_image_url ?? null,
      isGroup: chat.is_group,
      snippet: chat.last_message?.content ?? '',
      timestamp: new Date(chat.last_message?.created_at ?? chat.updated_at).getTime() || 0,
      unreadCount: chat.unread_count ?? 0,
      raw: chat,
    }));
  }, [teamChats, options.includeTeam, userProfile?.id]);

  const metaUnreadIds = useMemo(
    () => new Set(metaConversations.filter((c) => metaIsUnread(c)).map((c) => c.id)),
    // `metaReads` e' la dipendenza vera: isUnread legge lo store, non cambia identita'.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metaConversations, metaReads]
  );

  const items = useMemo(
    () =>
      mergeChatLists(
        options.includeWhatsApp ? (whatsappChats as unknown as WhatsAppChatLike[]) : [],
        metaAvailable ? metaConversations : [],
        teamItems,
        metaNames,
        metaUnreadIds
      ),
    [whatsappChats, metaConversations, teamItems, metaNames, metaUnreadIds, metaAvailable, options.includeWhatsApp]
  );

  // Si contano le CONVERSAZIONI da leggere, non i messaggi: prima WhatsApp contava i
  // messaggi ("99+") e Instagram/Messenger le conversazioni ("1"), e "Tutte" li
  // sommava. Il numero sulle schede risponde a "quante chat devo aprire".
  // Le chat silenziate non contano, come nel numero sul menu.
  const counts = useMemo(() => {
    const now = Date.now();
    return countUnreadByChannel(items, (key) => isMuted(chatPrefs.get(key), now));
  }, [items, chatPrefs]);

  return { items, counts };
}
